// Persists Contacts audience-sync tracking independently from the Contacts page.
// This lets the app-level tracker finish the user feedback after navigation or a
// browser refresh, without storing any provider credentials or contact data.

export const CONTACTS_SYNC_JOBS_CHANGED = "contacts-sync-jobs:changed";
export const CONTACTS_SYNC_JOB_STATES_RECEIVED =
  "contacts-sync-jobs:states-received";
export const CONTACTS_SYNC_JOB_TERMINAL = "contacts-sync-jobs:terminal";

const KEY_PREFIX = "ls_contacts_sync_jobs:";
const MAX_AGE_MS = 72 * 60 * 60 * 1000;
const PLATFORMS = new Set(["linkedin", "google", "meta"]);

function storageKey(accountId) {
  return `${KEY_PREFIX}${accountId}`;
}

function dispatchChange() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(CONTACTS_SYNC_JOBS_CHANGED));
}

export function publishContactsSyncJobStates(accountId, states) {
  if (
    !accountId ||
    !states ||
    typeof states !== "object" ||
    typeof window === "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CONTACTS_SYNC_JOB_STATES_RECEIVED, {
      detail: { accountId: Number(accountId), states },
    })
  );
}

export function publishContactsSyncJobTerminal(accountId, platform, state) {
  if (
    !accountId ||
    !PLATFORMS.has(platform) ||
    !state ||
    typeof state !== "object" ||
    typeof window === "undefined"
  ) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(CONTACTS_SYNC_JOB_TERMINAL, {
      detail: {
        accountId: Number(accountId),
        platform,
        state: { ...state, platform, active: false },
      },
    })
  );
}

export function reconcileContactsSyncJobState(currentState, terminalState) {
  const current = currentState || { active: false, status: "idle" };
  const currentJobId = Number(current?.jobId || 0);
  const terminalJobId = Number(terminalState?.jobId || 0);

  if (!terminalJobId || !currentJobId) {
    return { state: current, terminalState };
  }
  if (terminalJobId !== currentJobId) {
    return { state: current, terminalState: null };
  }

  return {
    state: { ...terminalState, active: false },
    terminalState,
  };
}

function readStore(accountId) {
  if (!accountId) return {};
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(accountId)) || "{}");
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch (_) {
    return {};
  }
}

function writeStore(accountId, value) {
  if (!accountId) return;
  try {
    if (Object.keys(value).length === 0) {
      localStorage.removeItem(storageKey(accountId));
    } else {
      localStorage.setItem(storageKey(accountId), JSON.stringify(value));
    }
  } catch (_) {
    // Local storage may be unavailable in private browser contexts. The current
    // Contacts view still observes the job while it remains mounted.
  }
}

export function getTrackedContactsSyncPlatforms(accountId) {
  const now = Date.now();
  const stored = readStore(accountId);
  const active = {};

  Object.entries(stored).forEach(([platform, value]) => {
    if (!PLATFORMS.has(platform) || !value?.startedAt) return;
    if (now - Number(value.startedAt) <= MAX_AGE_MS) {
      active[platform] = value;
    }
  });

  if (Object.keys(active).length !== Object.keys(stored).length) {
    writeStore(accountId, active);
  }

  return active;
}

export function trackContactsSyncJob(accountId, platform, jobId = null) {
  if (!accountId || !PLATFORMS.has(platform)) return;
  const stored = getTrackedContactsSyncPlatforms(accountId);
  stored[platform] = {
    startedAt: stored[platform]?.startedAt || Date.now(),
    jobId: jobId || stored[platform]?.jobId || null,
  };
  writeStore(accountId, stored);
  dispatchChange();
}

export function clearTrackedContactsSyncJob(accountId, platform) {
  if (!accountId || !PLATFORMS.has(platform)) return;
  const stored = getTrackedContactsSyncPlatforms(accountId);
  if (!stored[platform]) return;
  delete stored[platform];
  writeStore(accountId, stored);
  dispatchChange();
}
